import { history } from 'umi';
import { makeObservable, action, runInAction, override } from 'mobx';
import spmSDK from '@shopeepay/payment-selection-sdk-h5';
import { useFormatMsg } from '@/utils/locale';
import { Toast } from 'antd-mobile';
import bridge from '@/utils/v2/bridge';
import Repayment from './api.interface';
import { RepaymentModel } from './repayment.model';
import { CommonService } from './common';
import {
  REPAYABLE_TYPE,
  REFACTOR_REPAY_STATUS,
  BR_REPAY_CHANNEL_ID,
  CodeHandlers,
  IOrder,
  IPageInfo,
  IRepayOrderItem,
  RepaymentMethod,
  RepaymentParams,
} from './repayment.interface';
import insight from '@/utils/v2/insight';
import common from '@/utils/v2/common';
import { sendUserAction, UserActions } from '@/utils/v2/idap';

const formatMessage = useFormatMsg();

export class RepaymentService extends CommonService {
  // BR 还款下单耗时可能远超 10s 的全局默认超时。nginx 侧连接最长只保持 1 分钟，
  // 超过即判超时，前端配再大也没意义，所以对齐到 1 分钟。
  // 两条下单路径都受它控制：独立收银台(2PC)走父类 triggerRepayBySpmCashier，选渠道(1PC)走下面的 triggerRepay
  protected proactiveRepayTimeoutMs = 60 * 1000;

  constructor() {
    super();
    makeObservable(this);
  }

  @override
  async getRepayInfo({ isForHome }: { isForHome: boolean }) {
    // 调用 summary 接口获取全量金额与 use_icp 字段（订单明细改为分页接口按需拉取），
    // use_icp 由后端感知灰度告诉前端是否使用独立收银台
    const shopeeRnBundleVersion = common.getShopeeRnBundleVersion()?.toString();
    const rnVersion = common.getRnVersion()?.toString();
    const { code, result } = await this.post(Repayment.API.get_repay_summary, {
      body: {
        rn_bundle_version: shopeeRnBundleVersion,
        rn_version: rnVersion,
      },
      isUseDefaultErrorMsg: true,
    });
    if (code !== 0 && !isForHome) {
      history.push('/home');
      return false;
    }
    const {
      total_amount = 0,
      overflow_amount = 0,
      repayable_order_info_list = [],
      use_icp,
      is_large_order_volume = false,
    } = result;

    // 当 use_icp 等于 true 时，执行新的独立收银台还款逻辑
    this.isSpmCashierAvailable = use_icp;

    const overdueInfo = repayable_order_info_list?.find(
      (item: any) => item?.repayable_type === REPAYABLE_TYPE.OVERDUE,
    );
    const repayableInfo = repayable_order_info_list?.find(
      (item: any) => item?.repayable_type === REPAYABLE_TYPE.PARTIALREPAID,
    );

    // summary 接口不返回订单明细，homepage 需要展示最早一笔订单的到期时间，
    // 按当前展示优先级（先 overdue 后 repayable）拉取该类型的第一条订单补齐
    if (isForHome) {
      const homeInfo = [overdueInfo, repayableInfo].find((item: any) => item?.total_amount > 0);
      if (homeInfo) {
        const pageResult = await this.getRepayOrderPageList({
          repayable_type: homeInfo.repayable_type,
          page_index: 1,
          cursor: '',
          page_size: 1,
        });
        homeInfo.order_list = pageResult?.order_list ?? [];
      }
    }
    const list = (repayable_order_info_list || [])?.map((item: any) => {
      const { total_amount: totalAmount, repayable_type } = item;
      return {
        amount_to_pay: totalAmount,
        repayment_type: repayable_type,
      };
    });

    const info = {
      total_amount,
      overflow_amount,
      overdueInfo,
      repayableInfo,
      is_large_order_volume,
    } as RepaymentModel;

    // 当独立收银台不可用或者后端开关关闭时，执行原有逻辑
    if (!isForHome && !this.isSpmCashierAvailable) {
      const { code: code2, result: result2 } = await this.post(Repayment.API.get_repay_channel, {
        body: {
          list,
          overflow_amount,
        },
        isUseDefaultErrorMsg: true,
      });

      if (code2 !== 0) {
        return;
      }

      /**
       * 与SPM侧沟通确认上次还款成功渠道取default_selected_payment_channel字段展示
       */
      if (result2?.default_selected_payment_channel) {
        const lastRepayChannelInfo = JSON.parse(result2?.default_selected_payment_channel ?? '{}');
        console.log('lastRepayChannelInfo', lastRepayChannelInfo);
        info.payOption = result2?.default_selected_payment_channel ?? '';
        info.selectedChannelName = lastRepayChannelInfo?.channel_selected_display_text ?? '';
        info.selectedChannelId = lastRepayChannelInfo?.channel_id ?? 0;
      } else {
        info.payOption = '';
        info.selectedChannelName = '';
        info.selectedChannelId = 0;
      }
    }

    runInAction(() => {
      this.repayment = {
        ...this.repayment,
        ...info,
      };
    });
    return true;
  }

  /**
   * 分页拉取指定类型（Overdue / PartialRepaid）的可还款订单明细
   * cursor 传上一页最后一条订单的 repayment_plan_id，第一页传空字符串
   */
  @action
  async getRepayOrderPageList({
    repayable_type,
    page_index,
    cursor,
    page_size,
  }: {
    repayable_type: REPAYABLE_TYPE;
    page_index: number;
    cursor: string;
    page_size: number;
  }): Promise<{ order_list: IOrder[]; page_info?: IPageInfo } | null> {
    const shopeeRnBundleVersion = common.getShopeeRnBundleVersion()?.toString();
    const rnVersion = common.getRnVersion()?.toString();
    const { code, result } = await this.post(Repayment.API.get_repay_page_info, {
      body: {
        rn_bundle_version: shopeeRnBundleVersion,
        rn_version: rnVersion,
        repayable_type,
        pagination: {
          page_index,
          cursor,
          page_size,
        },
      },
      isUseDefaultErrorMsg: true,
    });
    if (code !== 0) {
      return null;
    }
    return {
      order_list: result?.order_list ?? [],
      page_info: result?.page_info,
    };
  }

  @action
  async getRepayOption({
    list,
    overflow_amount,
    payOption,
  }: {
    list: IRepayOrderItem[];
    overflow_amount: string;
    payOption: string;
  }) {
    Toast.show({ icon: 'loading', content: formatMessage({ id: 'loading' }), duration: 0 });
    const { code, result } = await this.post(Repayment.API.get_repay_channel, {
      body: {
        list,
        overflow_amount,
      },
      isUseDefaultErrorMsg: true,
    });

    if (code !== 0) {
      Toast.clear();
      return;
    }

    const {
      channels,
      encrypted_request_param,
      client_id,
      layout_format,
      recently_used_payment_channel,
      default_selected_payment_channel,
    } = result ?? {};

    try {
      sendUserAction(UserActions.ThirdSdk.CallPaymentSelectionSDK);
      // https://confluence.shopee.io/pages/viewpage.action?pageId=1511099481
      const paymentSdkInitData = {
        channels: JSON.parse(channels) ?? [],
        encryptedRequestParam: encrypted_request_param ?? '',
        clientId: client_id ?? 100035, // SHOPEE_CREDIT_FAST_ESCROW
        source: 'fastescrow',
        pageType: 'full' as const,
        layoutFormat: layout_format,
        // 默认选中的值，用于初始化的时候选中某一项,SPM FE说我们直接透传BE返给我们选中的渠道参数即可
        selectedPaymentChannelData: JSON.parse(
          default_selected_payment_channel || recently_used_payment_channel || '{}',
        ),
        onConfirm: (data: any) => {
          Toast.clear();
          // 点击confirm按钮的回调，data为用户选择的支付方式
          const { selectedPaymentChannelData, selectedPaymentLabelName } = data ?? {};
          console.log('spm sdk returns', data);
          // 配置告警和监控
          insight.reportThirdSDKCounter({
            sdkName: 'payment_selection_sdk',
            method: 'response',
          });
          insight.log({
            message: { selectedPaymentChannelData, selectedPaymentLabelName },
            name: 'payment_selection_sdk',
          });
          sendUserAction(UserActions.ThirdSdk.CallPaymentSelectionSDKResponse, {
            resp: data,
          });

          const channelDataJson = JSON.stringify(selectedPaymentChannelData);
          // 此次选择和上次选择不一样
          if (channelDataJson && channelDataJson !== payOption) {
            this.repayment = {
              ...this.repayment,
              payOption: channelDataJson,
              selectedChannelName: selectedPaymentLabelName,
              selectedChannelId:
                selectedPaymentChannelData?.channel_id ?? this.repayment.selectedChannelId,
            };
          }
        },
      };

      const sdk = await spmSDK.init(paymentSdkInitData); // 初始化SDK， data用来定义相关数据及方法，具体内容见下面描述
      sdk.launch(); // 拉起payment selection page 页面，可传初始化的一些值，用于修改init的内容，非必填参数
      Toast.clear();
    } catch (error) {
      sendUserAction(UserActions.ThirdSdk.CallPaymentSelectionSDKError, {
        error,
      });
      throw error;
    }
  }

  async finishRepayByShopeePayWebPage(repay_url: string) {
    await new Promise((resolve) => {
      bridge.callWebview(repay_url);
      const reappearCb = () => {
        resolve(true);
        bridge.unBind('viewWillReappear', reappearCb);
      };
      bridge.bind('viewWillReappear', reappearCb);
    });
  }

  @action
  async triggerRepay({
    overflow_amount,
    list,
    select_channel,
    repay_channel_name,
    is_icp_repay = RepaymentMethod.TRADITIONAL,
  }: RepaymentParams) {
    const payOption = select_channel;
    this.repayment.loading = true;

    const params = {
      overflow_amount,
      list,
      select_channel,
      repay_channel_name,
      is_icp_repay,
    };
    sendUserAction(UserActions.Repayment.CallTriggerRepayment, {
      params,
    });
    const response = await this.post(Repayment.API.proactive_repay, {
      body: params,
      isUseDefaultErrorMsg: true,
      ...this.proactiveRepayOptions,
    });
    sendUserAction(UserActions.Repayment.CallTriggerRepaymentResponse, {
      resp: response,
    });
    const { code, result } = response;

    const handleFinalState = (proactive_repay_id: string) => {
      this.repayment.loading = false;
      history.push({
        pathname: `/billing/repay-result/${proactive_repay_id ?? ''}`,
      });
    };

    const handleShopeePayOption = async (repay_url: string) => {
      this.repayment.loading = false;
      if (!repay_url) {
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'system_busy' }) });
        return;
      }
      // 如果返回的是rn地址，则跳转rn页面
      if (common.isRNLink(repay_url)) {
        bridge.navigateAppRL({
          apprl: repay_url,
          params: {
            __pc__: 1, // pop count, 1表示pop掉当前的webview
          },
        });
        return;
      }
      if (!common.isHttpLink(repay_url)) {
        // 如果都不是，说明跳转链接有问题，上报insight
        insight.log({
          name: 'trigger_upfront_repayment_error_link',
          message: {
            repay_url,
          },
        });
        sendUserAction(UserActions.Repayment.CallInvalidRepayUrl, {
          url: repay_url,
        });
      }
      // 如果不是RN，则返回的是http链接，新开webview跳转
      await this.finishRepayByShopeePayWebPage(repay_url);
      history.push({ pathname: '/home' });
    };

    const handleOtherRepayUrl = (repay_url: string) => {
      this.repayment.loading = false;
      window.location.href = repay_url;
    };

    const handleDefault = (proactive_repay_id: string) => {
      this.repayment.loading = false;
      history.push({
        pathname: `/billing/repay-result/${proactive_repay_id ?? ''}`,
      });
    };

    const codeHandlers: CodeHandlers = {
      0: () => {
        const { proactive_repay_id, repay_url, repay_status } = result;
        const isFinalState = [
          REFACTOR_REPAY_STATUS.CANCELLED,
          REFACTOR_REPAY_STATUS.FAILED,
          REFACTOR_REPAY_STATUS.DEDUCTED,
          REFACTOR_REPAY_STATUS.COMPLETE,
        ].includes(repay_status);

        const shopeePayOptions = [
          BR_REPAY_CHANNEL_ID.SHOPEE_PAY,
          BR_REPAY_CHANNEL_ID.SPINPAY_PIX,
          BR_REPAY_CHANNEL_ID.Santander_PIX,
        ];

        switch (true) {
          case isFinalState:
            // 与BE沟通确认以下这几种状态直接到终态（溢缴款全量抵扣）,直接跳转还款结果页
            handleFinalState(proactive_repay_id);
            break;
          case shopeePayOptions.some((option) => payOption?.includes(option)):
            // 如果是shopeePay方式，跳转spm rnsafe page
            handleShopeePayOption(repay_url);
            break;
          case repay_url:
            // 其他有repay_url，跳后台返回的url
            handleOtherRepayUrl(repay_url);
            break;
          default:
            // 直接跳还款结果页
            handleDefault(proactive_repay_id);
            break;
        }
      },
      default: () => {
        this.repayment.loading = false;
      },
    };

    const handler = codeHandlers[code] || codeHandlers.default;
    handler();
  }
}
